/**
 * CSV Import Modal for Email Subscribers
 * Allows importing subscribers from CSV file with column mapping
 *
 * Created: January 4, 2026
 */

import React, { useState } from "react";
import { Modal, Form, Button, Table, Alert, ProgressBar, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { importEmailSubscribers } from "../../redux/email-broadcast/emailBroadcastApi";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";

const CsvImportModal = ({ isOpen, onClose, listId, userInfoData, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: upload, 2: map columns, 3: preview, 4: import
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    email: "",
    name: "",
    phone: "",
  });
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [errors, setErrors] = useState([]);

  // Reset state when modal closes
  const handleClose = () => {
    setStep(1);
    setCsvFile(null);
    setCsvData([]);
    setHeaders([]);
    setColumnMapping({ email: "", name: "", phone: "" });
    setImporting(false);
    setImportResults(null);
    setErrors([]);
    onClose();
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setCsvFile(file);
    parseCSV(file);
  };

  // Parse CSV file using FileReader
  const parseCSV = (file) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length < 2) {
          toast.error("CSV file must have at least a header row and one data row");
          return;
        }

        // Parse headers
        const headerLine = lines[0];
        const parsedHeaders = parseCSVLine(headerLine);
        setHeaders(parsedHeaders);

        // Parse data rows
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length > 0 && values.some((v) => v.trim())) {
            const row = {};
            parsedHeaders.forEach((header, index) => {
              row[header] = values[index] || "";
            });
            rows.push(row);
          }
        }

        setCsvData(rows);

        // Auto-detect column mappings
        autoDetectColumns(parsedHeaders);

        setStep(2);
        toast.success(`Loaded ${rows.length} rows from CSV`);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse CSV file");
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
    };

    reader.readAsText(file);
  };

  // Parse a CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  // Auto-detect column mappings based on header names
  const autoDetectColumns = (headers) => {
    const mapping = { email: "", name: "", phone: "" };

    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase();

      if (lowerHeader.includes("email") || lowerHeader.includes("e-mail")) {
        mapping.email = header;
      } else if (lowerHeader.includes("name") || lowerHeader.includes("full name")) {
        mapping.name = header;
      } else if (
        lowerHeader.includes("phone") ||
        lowerHeader.includes("mobile") ||
        lowerHeader.includes("contact")
      ) {
        mapping.phone = header;
      }
    });

    setColumnMapping(mapping);
  };

  // Validate column mapping
  const validateMapping = () => {
    if (!columnMapping.email) {
      toast.error("Email column is required");
      return false;
    }

    // Check if email column has valid data
    const hasValidEmail = csvData.some((row) => {
      const email = row[columnMapping.email];
      return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    });

    if (!hasValidEmail) {
      toast.error("No valid email addresses found in the selected email column");
      return false;
    }

    return true;
  };

  // Proceed to preview step
  const handleProceedToPreview = () => {
    if (!validateMapping()) return;
    setStep(3);
  };

  // Get preview data (first 5 rows)
  const getPreviewData = () => {
    return csvData.slice(0, 5).map((row) => ({
      email: row[columnMapping.email] || "",
      name: row[columnMapping.name] || "",
      phone: row[columnMapping.phone] || "",
    }));
  };

  // Validate email format
  const isValidEmail = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Import subscribers
  const handleImport = async () => {
    setImporting(true);
    setErrors([]);

    try {
      // Map CSV data to subscribers array
      const subscribers = csvData
        .map((row) => ({
          email: row[columnMapping.email] ? row[columnMapping.email].trim() : "",
          name: row[columnMapping.name] ? row[columnMapping.name].trim() : "",
          phone: row[columnMapping.phone] ? row[columnMapping.phone].trim() : "",
        }))
        .filter((sub) => sub.email); // Remove rows without email

      if (subscribers.length === 0) {
        toast.error("No valid subscribers to import");
        setImporting(false);
        return;
      }

      // Call API
      const response = await importEmailSubscribers(
        { list_id: listId, subscribers },
        userInfoData?.token
      );

      if (response?.status) {
        setImportResults(response.data);
        setStep(4);
        toast.success(response.message || "Import completed");

        // Call success callback after a short delay
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1500);
      } else {
        toast.error(response?.message || "Import failed");
        if (response?.data?.errors) {
          setErrors(response.data.errors);
        }
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Render step 1: File upload
  const renderUploadStep = () => (
    <div className="p-3">
      <Alert variant="info">
        <strong>CSV Format:</strong>
        <br />
        Your CSV file should have headers in the first row. Required: <code>email</code>
        <br />
        Optional: <code>name</code>, <code>phone</code>
        <br />
        <br />
        <strong>Example:</strong>
        <pre className="mb-0">
          email,name,phone
          <br />
          john@example.com,John Doe,9876543210
          <br />
          jane@example.com,Jane Smith,8765432109
        </pre>
      </Alert>

      <Form.Group className="mb-3">
        <Form.Label>Select CSV File</Form.Label>
        <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
      </Form.Group>

      <div className="d-flex gap-2">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </div>
    </div>
  );

  // Render step 2: Column mapping
  const renderMappingStep = () => (
    <div className="p-3">
      <Alert variant="success">
        <strong>CSV Loaded:</strong> {csvData.length} rows found
      </Alert>

      <p className="mb-3">Map your CSV columns to subscriber fields:</p>

      <Form.Group className="mb-3">
        <Form.Label>
          Email Column <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          value={columnMapping.email}
          onChange={(e) => setColumnMapping({ ...columnMapping, email: e.target.value })}
        >
          <option value="">-- Select Email Column --</option>
          {headers.map((header) => (
            <option key={header} value={header}>
              {header}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">Required field containing email addresses</Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Name Column</Form.Label>
        <Form.Select
          value={columnMapping.name}
          onChange={(e) => setColumnMapping({ ...columnMapping, name: e.target.value })}
        >
          <option value="">-- Skip this field --</option>
          {headers.map((header) => (
            <option key={header} value={header}>
              {header}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">Optional: Subscriber name</Form.Text>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Phone Column</Form.Label>
        <Form.Select
          value={columnMapping.phone}
          onChange={(e) => setColumnMapping({ ...columnMapping, phone: e.target.value })}
        >
          <option value="">-- Skip this field --</option>
          {headers.map((header) => (
            <option key={header} value={header}>
              {header}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">Optional: Phone number</Form.Text>
      </Form.Group>

      <div className="d-flex gap-2">
        <Button variant="secondary" onClick={() => setStep(1)}>
          Back
        </Button>
        <Button variant="primary" onClick={handleProceedToPreview}>
          Next: Preview
        </Button>
      </div>
    </div>
  );

  // Render step 3: Preview
  const renderPreviewStep = () => {
    const previewData = getPreviewData();
    const validCount = csvData.filter((row) =>
      isValidEmail(row[columnMapping.email])
    ).length;
    const invalidCount = csvData.length - validCount;

    return (
      <div className="p-3">
        <Alert variant="warning">
          <strong>Preview:</strong> First 5 rows of {csvData.length} total rows
        </Alert>

        <div className="mb-3">
          <Badge bg="success" className="me-2">
            Valid: {validCount}
          </Badge>
          {invalidCount > 0 && (
            <Badge bg="danger">
              Invalid/Skipped: {invalidCount}
            </Badge>
          )}
        </div>

        <Table bordered hover responsive className="mb-4">
          <thead className="table-light">
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {previewData.map((row, index) => (
              <tr key={index}>
                <td>{row.email}</td>
                <td>{row.name || "-"}</td>
                <td>{row.phone || "-"}</td>
                <td>
                  {isValidEmail(row.email) ? (
                    <Badge bg="success">Valid</Badge>
                  ) : (
                    <Badge bg="danger">Invalid Email</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Alert variant="info">
          <strong>Note:</strong>
          <ul className="mb-0 mt-2">
            <li>Rows with invalid email addresses will be skipped</li>
            <li>Duplicate emails in the list will be skipped</li>
            <li>Emails in the suppression list (bounced/complained) will be skipped</li>
          </ul>
        </Alert>

        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => setStep(2)}>
            Back
          </Button>
          <Button variant="primary" onClick={handleImport} disabled={importing || validCount === 0}>
            {importing ? "Importing..." : `Import ${validCount} Subscribers`}
          </Button>
        </div>
      </div>
    );
  };

  // Render step 4: Results
  const renderResultsStep = () => (
    <div className="p-3">
      <Alert variant={importResults?.added > 0 ? "success" : "warning"}>
        <h5>Import Complete</h5>
      </Alert>

      <div className="mb-4">
        <div className="d-flex justify-content-between mb-2">
          <span>Successfully Imported:</span>
          <Badge bg="success" className="fs-6">
            {importResults?.added || 0}
          </Badge>
        </div>
        <div className="d-flex justify-content-between mb-2">
          <span>Skipped (Duplicates/Invalid):</span>
          <Badge bg="warning" className="fs-6">
            {importResults?.skipped || 0}
          </Badge>
        </div>
        <div className="d-flex justify-content-between">
          <span>Total Rows:</span>
          <Badge bg="secondary" className="fs-6">
            {csvData.length}
          </Badge>
        </div>
      </div>

      {importResults?.errors && importResults.errors.length > 0 && (
        <Alert variant="danger">
          <strong>Errors ({importResults.errors.length}):</strong>
          <ul className="mb-0 mt-2">
            {importResults.errors.slice(0, 5).map((err, index) => (
              <li key={index}>
                {err.email}: {err.error}
              </li>
            ))}
            {importResults.errors.length > 5 && (
              <li>... and {importResults.errors.length - 5} more</li>
            )}
          </ul>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert variant="danger">
          <strong>Errors:</strong>
          <ul className="mb-0 mt-2">
            {errors.slice(0, 5).map((err, index) => (
              <li key={index}>
                {err.email}: {err.error}
              </li>
            ))}
            {errors.length > 5 && <li>... and {errors.length - 5} more</li>}
          </ul>
        </Alert>
      )}

      <div className="d-flex gap-2">
        <Button variant="primary" onClick={handleClose} className="flex-grow-1">
          Done
        </Button>
        <Button variant="outline-secondary" onClick={() => setStep(1)}>
          Import Another File
        </Button>
      </div>
    </div>
  );

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
      <div className="createNewCTOModalContainer">
        <ModalHeadAndClose
          title={`CSV Import - Step ${step} of 4`}
          onClose={handleClose}
        />
        {step === 1 && renderUploadStep()}
        {step === 2 && renderMappingStep()}
        {step === 3 && renderPreviewStep()}
        {step === 4 && renderResultsStep()}
      </div>
    </Modal>
  );
};

export default CsvImportModal;
